import React, { useState } from "react";
import axios from "axios";
import { Loader2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL || "";

// The rule the server enforces (routers/auth.py change-password), shown up front.
export function passwordProblem(password) {
  if (password.length < 8) return "לפחות 8 תווים";
  if (!/\p{L}/u.test(password) || !/[^\p{L}]/u.test(password)) return "לפחות אות אחת ותו אחד שאינו אות (ספרה או סימן)";
  const digits = password.replace(/[\s./-]/g, "");
  if (/^\d+$/.test(digits) && (digits.length === 6 || digits.length === 8)) return "אי אפשר להשתמש בתאריך כסיסמה";
  return "";
}

// The logged-in user replaces her own password. `blocking`: the required
// change is overdue — the dialog cannot be closed without changing.
export default function ChangePasswordDialog({ open, onClose, onChanged, blocking = false }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const problem = form.next ? passwordProblem(form.next) : "";
  const mismatch = form.confirm && form.next !== form.confirm;
  const inputCls = "w-full h-10 rounded-md border border-stone-200 bg-white px-3 text-sm";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (problem || mismatch || !form.current) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/change-password`,
        { current_password: form.current, new_password: form.next }, { withCredentials: true });
      setForm({ current: "", next: "", confirm: "" });
      onChanged?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((typeof detail === "object" ? detail.message : detail) || "החלפת הסיסמה נכשלה, נא לנסות שוב");
    } finally {
      setSaving(false);
    }
  };

  const keepOpen = (e) => { if (blocking) e.preventDefault(); };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !blocking) onClose?.(); }}>
      <DialogContent dir="rtl" className={`max-w-md text-right ${blocking ? "[&>button:last-child]:hidden" : ""}`}
                     onEscapeKeyDown={keepOpen} onPointerDownOutside={keepOpen} data-testid="change-password-dialog">
          <div dir="rtl" className="space-y-4 text-right">
        <DialogHeader className="text-right sm:text-right">
          <DialogTitle className="flex items-center gap-2"><KeyRound size={18} /> החלפת סיסמה</DialogTitle>
          <DialogDescription className="text-right">הסיסמה החדשה: לפחות 8 תווים, אות אחת ותו אחד שאינו אות. לא תאריך לידה.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><label className="text-xs font-semibold text-[#1E3F33]">סיסמה נוכחית</label>
            <input type="password" autoComplete="current-password" className={inputCls} value={form.current}
                   onChange={set("current")} required data-testid="cp-current" /></div>
          <div><label className="text-xs font-semibold text-[#1E3F33]">סיסמה חדשה</label>
            <input type="password" autoComplete="new-password" className={inputCls} value={form.next}
                   onChange={set("next")} required data-testid="cp-new" />
            {problem && <div className="mt-1 text-xs text-red-600">{problem}</div>}</div>
          <div><label className="text-xs font-semibold text-[#1E3F33]">אימות הסיסמה החדשה</label>
            <input type="password" autoComplete="new-password" className={inputCls} value={form.confirm}
                   onChange={set("confirm")} required data-testid="cp-confirm" />
            {mismatch && <div className="mt-1 text-xs text-red-600">הסיסמאות אינן זהות</div>}</div>
          {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          <div className="flex gap-2">
            <Button type="submit" disabled={saving || !!problem || !!mismatch} className="flex-1 gap-2 text-white"
                    style={{ background: "#1E3F33" }} data-testid="cp-submit">
              {saving && <Loader2 className="animate-spin" size={16} />} שמירת הסיסמה החדשה
            </Button>
            {!blocking && <Button type="button" variant="outline" onClick={onClose}>ביטול</Button>}
          </div>
        </form>
          </div>
      </DialogContent>
    </Dialog>
  );
}
